/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DasPowerDetectorChart from "./DasPowerDetectorChart";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  createPowerDetectorChartConfig,
  buildPowerDetectorParams,
  PowerDetectorParams,
} from "./dasChartUtils";
import { formatOnOff } from "../utils";

const RiuDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
  siteObjectKey,
}) => {
  const riuAlarms = parsedAttributes.riuAlarms;
  const riuAlarmsMask = parsedAttributes.riuAlarmsMask;
  const hasAlarms = riuAlarms !== undefined || riuAlarmsMask !== undefined;
  const riuRfAlarm = Array.isArray(parsedAttributes.riuRfAlarm)
    ? parsedAttributes.riuRfAlarm
    : [];
  const riuRfAlarmSorted = [...riuRfAlarm].sort(
    (a, b) => Number(a?.riuChannelNumber) - Number(b?.riuChannelNumber)
  );
  const hasRfAlarm = riuRfAlarmSorted.length > 0;
  const isDisconnected = statusLabel === "Disconnected";

  const alarmDefinitions: AlarmDefinition[] = [
    { bit: 1, name: "Overall Alarm" },
    { bit: 2, name: "Fan Fault Alarm" },
    { bit: 3, name: "Temperature Alarm" },
    { bit: 4, name: "DC Voltage Lower Alarm" },
    { bit: 5, name: "Firmware Mismatch Alarm" },
    
  ];

  const ports = [1, 2, 3, 4, 5, 6, 7, 8];
  const riuDlIndicators = ports.map((port) => `riuDlPwrIn${port}`);
  const hasRfInfo = ports.some((port) => (
    parsedAttributes[`riuBandSwitch${port}`] !== undefined ||
    parsedAttributes[`riuDlPwrIn${port}`] !== undefined
  ));
  const hasDlPowerChart = ports.some((port) => (
    parsedAttributes[`riuDlPwrIn${port}`] !== undefined &&
    parsedAttributes[`riuDlPwrIn${port}`] !== null &&
    parsedAttributes[`riuDlPwrIn${port}`] !== ""
  ));

  const remoteEquipmentObjectKey = useMemo(() => {
    return (
      device.objectKey ||
      parsedAttributes.objectKey ||
      parsedAttributes.manageObject ||
      parsedAttributes.managedObject ||
      parsedAttributes.objectId ||
      "RIU-1"
    );
  }, [
    device.objectKey,
    parsedAttributes.objectKey,
    parsedAttributes.manageObject,
    parsedAttributes.managedObject,
    parsedAttributes.objectId,
  ]);

  const serviceDeviceObjectKey =
    siteObjectKey || device.servicesDeviceObjectKey || device.serialNumber || "";

  const dlPowerChartParams = useMemo<PowerDetectorParams | null>(() => {
    if (!hasDlPowerChart) return null;

    return {
      remoteEquipmentObjectKey,
      indicators: riuDlIndicators,
      bandNames: ports.map((port) => `Port ${port}`),
      bandNumbers: ports,
      yAxisMin: -50,
      yAxisMax: 50,
    };
  }, [hasDlPowerChart, remoteEquipmentObjectKey]);

  const dlPowerChartConfig = useMemo(
    () =>
      dlPowerChartParams
        ? createPowerDetectorChartConfig("dl", dlPowerChartParams)
        : null,
    [dlPowerChartParams]
  );

  const dlPowerChartAdditionalParams = useMemo(
    () =>
      dlPowerChartParams
        ? {
            ...buildPowerDetectorParams(
              serviceDeviceObjectKey,
              remoteEquipmentObjectKey,
              riuDlIndicators
            ),
          }
        : null,
    [dlPowerChartParams, serviceDeviceObjectKey, remoteEquipmentObjectKey]
  );

  const [fullscreenChart, setFullscreenChart] = useState<"dl" | null>(null);

  const handleOpenFullscreen = () => {
    setFullscreenChart("dl");
  };

  const handleCloseFullscreen = () => {
    setFullscreenChart(null);
  };

  if (alarmsOnly) {
    if (!hasAlarms) return null;

    return (
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">Alarms</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <AlarmTable
            alarms={riuAlarms}
            alarmsMask={riuAlarmsMask}
            alarmDefinitions={alarmDefinitions}
            isDisconnected={isDisconnected}
          />
        </CardContent>
      </Card>
    );
  }

  if (!hasRfInfo && !hasAlarms && !hasRfAlarm) return null;

  const formatRiuPowerNoUnit = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toFixed(2);
  };

  return (
    <>
      {hasRfInfo && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">RF Information</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[220px]">Parameter</TableHead>
                    {ports.map((port) => (
                      <TableHead key={port} className="min-w-[90px] text-center">Port {port}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Switch</TableCell>
                    {ports.map((port) => (
                      <TableCell key={`switch-${port}`} className="text-center">
                        {formatOnOff(parsedAttributes[`riuBandSwitch${port}`])}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Downlink Power</TableCell>
                    {ports.map((port) => (
                      <TableCell key={`dl-${port}`} className="text-center">
                        {formatRiuPowerNoUnit(parsedAttributes[`riuDlPwrIn${port}`])}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {dlPowerChartConfig && dlPowerChartAdditionalParams && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">DL Power Chart</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <div style={{ height: 300, width: "100%" }}>
              <DasPowerDetectorChart
                config={{
                  ...dlPowerChartConfig,
                  title: "DL Power",
                  csvFilename: "riu_dl_power",
                  chartImageFilename: "riu_dl_power_chart",
                }}
                onFullscreenToggle={handleOpenFullscreen}
                isFullscreen={false}
                additionalParams={dlPowerChartAdditionalParams}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {hasRfAlarm && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">RF Alarm</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {riuRfAlarmSorted.map((rfAlarm: any, index: number) => {
                const isAlarmActive = Number(rfAlarm?.riuRfAlarms) > 0;

                return (
                  <div
                    key={`${rfAlarm?.riuChannelNumber ?? index}`}
                    className="rounded-xl border p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">
                        Channel {rfAlarm?.riuChannelNumber ?? index + 1}
                      </div>

                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: isAlarmActive ? "#ef4444" : "#22c55e",
                          boxShadow: `0 0 8px ${isAlarmActive ? "#ef4444" : "#22c55e"}`,
                        }}
                      />
                    </div>

                    <div className="mt-3 text-xs text-muted-foreground">
                      Status:{" "}
                      <span className="font-medium text-foreground">
                        {isAlarmActive ? "Alarm Active" : "Normal"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {hasAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={riuAlarms}
              alarmsMask={riuAlarmsMask}
              alarmDefinitions={alarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      <Dialog
        open={fullscreenChart === "dl"}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: "1px solid var(--border)",
            borderRadius: "12px",
            minHeight: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid var(--border)",
            pb: 2,
            px: 4,
          }}
        >
         
          <IconButton onClick={handleCloseFullscreen} size="small" sx={{ color: "var(--foreground)" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {dlPowerChartConfig && dlPowerChartAdditionalParams && (
            <DasPowerDetectorChart
              config={{
                ...dlPowerChartConfig,
                title: "DL Power",
                csvFilename: "riu_dl_power",
                chartImageFilename: "riu_dl_power_chart",
              }}
              onFullscreenToggle={handleCloseFullscreen}
              isFullscreen={true}
              additionalParams={dlPowerChartAdditionalParams}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RiuDeviceDetails;